import api from "../../config/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_ROUTES } from "../../config/apiRoutes";

/**✅ getLevels Thunk */
export const getLeadLevelsThunk = createAsyncThunk(
    "leadManagement/getLeadLevelsThunk",
    async (_, { rejectWithValue, getState }) => {

        const state = getState();

        // 👈 Skip API if data already in state
        if (state.leadManagement?.leadLevels?.length > 0) {
            return state.leadManagement.leadLevels;
        }

        try {
            const response = await api.post(API_ROUTES.LEVELS);

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }
            return rejectWithValue(message || "Failed to fetch Levels");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);

/**✅ getCallStatusTypesThunk Thunk */
export const getCallStatusTypesThunk = createAsyncThunk(
    "leadManagement/getCallStatusTypesThunk",
    async (_, { rejectWithValue, getState }) => {

        const state = getState();

        // 👈 Skip API if data already in state
        if (state.leadManagement?.callStatusTypes?.length > 0) {
            return state.leadManagement.callStatusTypes;
        }

        try {
            const response = await api.post(API_ROUTES.CALL_STATUS_TYPES);

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }
            return rejectWithValue(message || "Failed to fetch Call Status Types");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);

/**✅ getLeadManagementThunk Thunk */
export const getLeadManagementThunk = createAsyncThunk(
    "leadManagement/getLeadManagementThunk",
    async (payload = {}, { rejectWithValue, getState }) => {
        const state = getState();
        const user = state.authentication?.user;

        // 👈 Skip API if data already in state
        if (payload.type !== "getLeads") {
            if (state.leadManagement?.getLeadManagementLeads?.length > 0) {
                return state.leadManagement.getLeadManagementLeads;
            }
        }

        try {
            const response = await api.post(API_ROUTES.LEAD_MANAGEMENT_LEADS, {
                employee_id: user?.user_id,
            });

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                // API now returns the lead's branch as `branch_id` (shared with the external API);
                // the lead screens filter on `branch_department_id`, so map it back.
                return (data || []).map((item) => ({
                    ...item,
                    branch_department_id: item.branch_department_id ?? item.branch_id ?? null,
                }));
            }

            return rejectWithValue(message || "Failed to fetch branch departments");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);

/**✅ addNewAssigningLeadsThunk **/
export const addNewAssigningLeadsThunk = createAsyncThunk(
    "leadManagement/addNewAssigningLeadsThunk",
    async ({ credentials }, { rejectWithValue, getState, dispatch }) => {
        const state = getState();
        const user = state.authentication?.user;

        const payload = {
            ...credentials,
            employee_id: user?.user_id,
        };

        try {
            const response = await api.post(API_ROUTES.CREATENEW_UNASSIGN_LEADS, payload);
            const { message, statusCode } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                };
            }

            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);

/**✅ update unassigning leads Thunk **/
export const updateUnAssigningLeadsThunk = createAsyncThunk(
    "leadManagement/updateUnAssigningLeadsThunk",
    async ({ credentials }, { rejectWithValue, getState, dispatch }) => {
        const state = getState();
        const user = state.authentication?.user;

        const payload = {
            ...credentials,
            employee_id: user?.user_id,
        };

        try {
            const response = await api.post(API_ROUTES.UPDATE_UNASSIGN_LEADS, payload);
            const { message, statusCode } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                };
            }

            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);

/**✅ getMyTaskThunk */
export const getMyTaskThunk = createAsyncThunk(
    "leadManagement/getMyTaskThunk",
    async (payload = {}, { rejectWithValue, getState }) => {
        const state = getState();
        const user = state.authentication?.user;

        // 👈 Skip API if data already in state
        if (payload.type !== "getTask") {
            if (state.leadManagement?.getMyTaskDetails?.length > 0) {
                return state.leadManagement.getMyTaskDetails;
            }
        }

        try {
            const response = await api.post(API_ROUTES.MYTASK, {
                employee_id: user?.user_id,
            });

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }

            return rejectWithValue(message || "Failed to fetch branch departments");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);

/**✅ get Lead History Thunk */
export const getLeadHistoryThunk = createAsyncThunk(
    "leadManagement/getLeadHistoryThunk",
    async ({ credentials }, { rejectWithValue }) => {

        try {
            const response = await api.post(API_ROUTES.LEAD_HISTORY, credentials);

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }

            return rejectWithValue(message || "Failed to fetch branch departments");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);

/**✅ addNewAssigningLeadsThunk **/
export const updateLeadHistoryThunk = createAsyncThunk(
    "leadManagement/updateLeadHistoryThunk",
    async ({ credentials }, { rejectWithValue, getState, dispatch }) => {
        const state = getState();
        const user = state.authentication?.user;

        const payload = {
            ...credentials,
            employee_id: user?.user_id,
        };

        try {
            const response = await api.post(API_ROUTES.LEAD_HISTORY_UPDATE, payload);
            const { message, statusCode } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                };
            }

            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);

/**✅ getLeadStatusTypesThunk  */
export const getLeadStatusTypesThunk = createAsyncThunk(
    "leadManagement/getLeadStatusTypesThunk",
    async (_, { rejectWithValue, getState }) => {
        const state = getState();
        const user = state.authentication?.user;

        // 👈 Skip API if data already in state
        if (state.leadManagement?.leadStatusTypes?.length > 0) {
            return state.leadManagement.leadStatusTypes;
        }

        try {
            const response = await api.post(API_ROUTES.LEAD_STATUS_TYPES, {
                employee_id: user?.user_id,
            });

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }

            return rejectWithValue(message || "Failed to fetch branch departments");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);

/**✅ getHeadLevelEmployeeThunk  */
export const getHeadLevelEmployeeThunk = createAsyncThunk(
    "leadManagement/getHeadLevelEmployeeThunk",
    async ({ credentials }, { rejectWithValue, getState, dispatch }) => {
        const state = getState();
        const user = state.authentication?.user;

        const payload = {
            ...credentials,
            employee_id: user?.user_id,
        };

        try {
            const response = await api.post(API_ROUTES.LEVEL_HEAD_EMPLOYEES, payload);

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                    data
                };
            }

            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);

/**✅ getLevelWiseEmployeesThunk  */
export const getLevelWiseEmployeesThunk = createAsyncThunk(
    "leadManagement/getLevelWiseEmployeesThunk",
    async ({ credentials }, { rejectWithValue }) => {

        const payload = {
            ...credentials,
        };

        try {
            const response = await api.post(API_ROUTES.LEVEL_WISE_EMPLOYEES, payload);

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                    data
                };
            }


            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);

/**✅ getIndividualUserTaskThunk  */
export const getIndividualUserTaskThunk = createAsyncThunk(
    "leadManagement/getIndividualUserTaskThunk",
    async (payload = {}, { rejectWithValue, getState }) => {
        const state = getState();
        const user = state.authentication?.user;

        // 👈 Skip API if data already in state
        // if (payload.type !== "getTask") {
            if (state.leadManagement?.getIndividualUserTaskDetails?.length > 0) {
                return state.leadManagement.getIndividualUserTaskDetails;
            }
        // }

        try {
            const response = await api.post(API_ROUTES.INDIVIDUAL_USER_TASK, {
                employee_id: user?.user_id,
            });

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }

            return rejectWithValue(message || "Failed to fetch branch departments");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);







export const getUnAssignedEmployeeesThunk = createAsyncThunk(
    "leadManagement/getUnAssignedEmployeeesThunk",
    async ({ credentials }, { rejectWithValue }) => {

        try {
            const response = await api.post(API_ROUTES.UN_ASSIGNED_EMPLOYEES, credentials);

            const { message, statusCode, data } = response.data;

            if (statusCode === 200) {
                return data;
            }

            return rejectWithValue(message || "Failed to fetch un-assigned Employees");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Request failed"
            );
        }
    }
);


/**✅ addLevelEmployeesThunk **/
export const addLevelEmployeesThunk = createAsyncThunk(
    "leadManagement/addLevelEmployeesThunk",
    async ({ credentials }, { rejectWithValue, getState, dispatch }) => {
        const state = getState();
        const user = state.authentication?.user;

        const payload = {
            ...credentials,
            employee_id: user?.user_id,
        };

        try {
            const response = await api.post(API_ROUTES.ADD_LEVEL_EMPLOYEES, payload);
            const { message, statusCode } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                };
            }

            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);

/**✅ deleteLevelEmployeesThunk **/
export const deleteLevelEmployeesThunk = createAsyncThunk(
    "leadManagement/deleteLevelEmployeesThunk",
    async ({ credentials }, { rejectWithValue, getState, dispatch }) => {
        const state = getState();
        const user = state.authentication?.user;

        const payload = {
            ...credentials,
            employee_id: user?.user_id,
        };

        try {
            const response = await api.post(API_ROUTES.DELETE_LEVEL_EMPLOYEES, payload);
            const { message, statusCode } = response.data;

            if (statusCode === 200) {
                return {
                    success: true,
                    message,
                };
            }

            return rejectWithValue(message || "Lead Assign failed");
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Lead Assign failed"
            );
        }
    }
);